import { useState, memo, useCallback } from 'react';
import './LiveContent.scss';
import iframeWithWhisper from '../iframe/WhisperIframe';
import Iframe from '../iframe/Iframe';
import ReactPlayer from 'react-player';
import SettingsService from '../../services/SettingsService';
import { getMakeConfig } from '../../utils/models';
import { getDeviceInfo } from '../../Upshow';

// To avoid re-renders when LiveContent component states/props changes
const MemoizedIframeComponent = memo(
    ({
        src,
        onDone,
        onError,
        onProgress,
        onHeartbeat,
        onPlay,
        className,
        disableWatchdog,
        whisperPlayer,
    }) => {
        const IframeComponent = iframeWithWhisper(Iframe);
        return (
            <IframeComponent
              className={className}
              disableWatchdog={disableWatchdog}
              whisperPlayer={whisperPlayer}
              onDone={onDone}
              onError={onError}
              onProgress={onProgress}
              onHeartbeat={onHeartbeat}
              onPlay={onPlay}
              src={src}
            />
        );
    }
);

export default function LiveContent(props) {
    const [placeholderVideoError, setPlaceholderVideoError] = useState(false);
    const videoLiveContentPlaceholder = getMakeConfig().liveContentState.videoPlaceholder;
    const comingSoonImage = 'https://cdn.upshow.tv/ui-assets/DAZN_Event_Coming.png';

    // Memoize callbacks to avoid re-renders
    const handleDone = useCallback(() => props.done(), [props.done]);
    const handleError = useCallback((error) => {props.error(error)}, [props.error]);
    const handleProgress = useCallback((progress) => {
    if (props.playerError) {
        props.solveError();
      }
    }, [props.playerError, props.solveError]);


    const handleHeartbeat = useCallback((data) => {
        if (props.onHeartbeat) {
            props.onHeartbeat(data);
        }
    }, [props.onHeartbeat]);

    if (!props?.state?.liveContent) return null;

    if (props.state.liveContent.status === 'COMING') {
        const VideoUrl = 'https://cdn.upshow.tv/ui-assets/DAZN-Live_event_is_coming.mp4';
        return <>
            {
                (!placeholderVideoError && videoLiveContentPlaceholder)
                    ? <ReactPlayer
                        width="100%"
                        height="100%"
                        onError={() => setPlaceholderVideoError(true)}
                        playing={true}
                        url={VideoUrl}
                        muted={false}
                        volume={1}
                        key={VideoUrl}
                        loop={true}
                    />
                    : <img width="100%" height="100%" src={comingSoonImage} alt="Event coming soon"/>
            }
        </>;
    } else if (props.state.liveContent.status === 'LIVE') {
        if (window.UPshowAndroidApp) { //we're in UPshow's android app, let the app take over
            return <img width="100%" height="100%" src={comingSoonImage} alt="Event coming soon" />
        }
        const metadata = props.state.liveContent.metadata ?? {};
        const token = props.state.liveContent.token ?? "";

        const url = new URL(metadata.url);
        if (props.settings.device_id && props.settings.device_id !== 'undefined') {
            url.searchParams.set('device_id', props.settings.device_id);
        }
        url.searchParams.set('evid', props.state.liveContent.id);

        if (token) {
            url.searchParams.set('token', token);
        }

        const withCC = SettingsService.getSetting('withCC');

        if (withCC) {
            url.searchParams.set("withCC", withCC);
        }

         const organizationId = SettingsService.getSetting("organization_id");
         const dataOrganization = SettingsService.getSetting("organization");
         const uiVersion = SettingsService.getSetting("uiVersion");

         const playerUrl = new URL(url.toString());


         playerUrl.searchParams.set("model", getDeviceInfo()?.model || "");

         const ownParams = new URLSearchParams(window.location.search);

         const xumoConnectionType = ownParams.get('xumo_connection_type');
         const xumoHasLink = ownParams.get('xumo_has_link');
         const xumoDeviceName = ownParams.get('xumo_device_name');
         const xumoDeviceId = ownParams.get('xumo_device_id');

         if (xumoConnectionType) playerUrl.searchParams.set('xumo_connection_type', xumoConnectionType);
         if (xumoHasLink) playerUrl.searchParams.set('xumo_has_link', xumoHasLink);
         if (xumoDeviceName) playerUrl.searchParams.set('xumo_device_name', xumoDeviceName);
         if (xumoDeviceId) playerUrl.searchParams.set('xumo_device_id', xumoDeviceId);

         if (process.env.REACT_APP_KIOSK_EXTENSION_ID) {
             playerUrl.searchParams.set("monitor", "1");
             playerUrl.searchParams.set("extension_id", process.env.REACT_APP_KIOSK_EXTENSION_ID);
         }

         if (props.state.liveContent.title) playerUrl.searchParams.set("title", encodeURIComponent(props.state.liveContent.title));
         if (organizationId) playerUrl.searchParams.set("organization_id", organizationId);
         if (dataOrganization) {
             let value = `${dataOrganization.id} (${dataOrganization.name})`

             playerUrl.searchParams.set("organization", value);
         }

         if (uiVersion && uiVersion !== "") {
            playerUrl.searchParams.set("uiVersion", uiVersion);
         }

        return (
            <>
                <MemoizedIframeComponent
                    className="LiveContent"
                    disableWatchdog={true}
                    whisperPlayer={true}
                    onDone={handleDone}
                    onError={handleError}
                    onProgress={handleProgress}
                    onHeartbeat={handleHeartbeat}
                    src={playerUrl}
                />
            </>
        );
  }

  return null;
}
